import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IPersonaProps } from '@fluentui/react';
import {
  Field,
  FluentProvider,
  Input,
  Radio,
  RadioGroup,
  Textarea,
  webLightTheme,
} from '@fluentui/react-components';
import React, { useEffect } from 'react';
import { Controller, FieldValues, UseFormMethods, useWatch } from 'react-hook-form';
import moment from 'moment';
import { yesOption, noOption } from '../../../utils/types/CommonDropdownOptions';
import UtcDatePicker from '../../common/utcDatePicker/UtcDatePicker';
import { IEntityTerminationActionItem } from '../types';
import ErrorHandlingPeoplePicker from '../../common/formFields/ErrorHandlingPeoplePicker';
import searchForUsers from '../../../utils/microsoftGraphApi/userSearch';
import { IServiceContact } from '../../companies/serviceContacts/interfaces';
import {
  getEarliestTaskCompletedDate,
  getLatestTaskCompletedDate,
  getMinDateCompleted,
  onlyWhenChanged,
  parseCalendarDate,
  validateDateCompleted,
  validateNotAfterMaxDate,
} from '../utils/taskCompletionDates';

interface IEntityTerminationActionItemEditFormProps {
  form: UseFormMethods<FieldValues>;
  selectedActionItem?: IEntityTerminationActionItem;
  /** When true, the description is shown read-only (e.g. template-based Finance Incomplete items). */
  isDescriptionReadOnly?: boolean;
}

const EntityTerminationActionItemEditForm = (
  props: IEntityTerminationActionItemEditFormProps,
): JSX.Element => {
  const { form, selectedActionItem, isDescriptionReadOnly = false } = props;
  const { control, errors, setValue } = form;

  const today = moment().endOf('day').toDate();

  const completed = useWatch({
    control,
    name: 'completed',
    defaultValue: !!selectedActionItem?.dateCompleted,
  });

  const dateCompleted = useWatch({
    control,
    name: 'dateCompleted',
    defaultValue: selectedActionItem?.dateCompleted ?? null,
  });

  const dateStarted = useWatch({
    control,
    name: 'dateStarted',
    defaultValue: selectedActionItem?.dateStarted ?? null,
  });

  const minCompletedDate = parseCalendarDate(dateStarted);

  const dateCompletedDate = parseCalendarDate(dateCompleted);

  const maxStartedDate = dateCompletedDate && dateCompletedDate < today ? dateCompletedDate : today;

  // The action item cannot be completed before the latest task Completed Date.
  const latestTaskCompletedDate = getLatestTaskCompletedDate(selectedActionItem?.tasks);

  // The action item's Date Started cannot be after the earliest child task's
  // Completed Date.
  const earliestTaskCompletedDate = getEarliestTaskCompletedDate(selectedActionItem?.tasks);
  const maxDateStarted =
    earliestTaskCompletedDate && earliestTaskCompletedDate < maxStartedDate
      ? earliestTaskCompletedDate
      : maxStartedDate;
  const validateStartNotAfterEarliestTaskCompleted = onlyWhenChanged(
    selectedActionItem?.dateStarted,
    validateNotAfterMaxDate(
      earliestTaskCompletedDate,
      'earliest task Completed Date',
      'Date Started',
    ),
  );

  const minDateCompleted = getMinDateCompleted(minCompletedDate, latestTaskCompletedDate);

  useEffect(() => {
    if (dateCompleted && !completed) {
      setValue('completed', true, { shouldValidate: true, shouldDirty: false });
    }
  }, [dateCompleted, completed, setValue]);

  const onResolveSuggestions = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return searchForUsers(filterText);
    }
    return [];
  };

  const hasIncompleteTasks = (selectedActionItem?.tasks ?? []).some((task) => !task?.completedDate);

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        {isDescriptionReadOnly ? (
          <Field label="Action Item Name">
            <Input
              id="description"
              value={selectedActionItem?.description ?? ''}
              disabled
              readOnly
            />
          </Field>
        ) : (
          <Controller
            control={control}
            name="description"
            defaultValue={selectedActionItem ? selectedActionItem.description : ''}
            rules={{
              required: 'Please enter action item description.',
            }}
            render={({ onChange, onBlur, value, name, ref }) => (
              <Field
                label="Action Item Description"
                required
                validationMessage={errors?.description?.message}
              >
                <Input
                  id="description"
                  value={value}
                  name={name}
                  ref={ref}
                  onBlur={onBlur}
                  onChange={(ev, data) => onChange(data.value)}
                />
              </Field>
            )}
          />
        )}
        <Controller
          control={control}
          name="dateStarted"
          defaultValue={selectedActionItem ? selectedActionItem.dateStarted : null}
          rules={{
            validate: {
              requiredWhenCompleted: (value) =>
                completed ? !!value || 'Date required when an action item is completed.' : true,
              notAfterEarliestTaskCompleted: validateStartNotAfterEarliestTaskCompleted,
            },
          }}
          render={({ onChange, value }) => (
            <UtcDatePicker
              label="Date Started"
              allowTextInput
              disableAutoFocus={false}
              isRequired={!!completed}
              value={value}
              textField={{ name: 'dateStarted', errorMessage: errors?.dateStarted?.message }}
              onSelectDate={(utcDateString: string) => onChange(utcDateString)}
              maxDate={maxDateStarted}
            />
          )}
        />
        <Controller
          name="owner"
          control={control}
          defaultValue={selectedActionItem?.owner ?? null}
          rules={{ required: 'Please add owner.' }}
          render={({ onChange, value }) => (
            <ErrorHandlingPeoplePicker
              controlName="owner"
              labelText="Owner"
              required
              itemLimit={1}
              selectedItems={value ? [value] : []}
              errorMessage={errors?.owner?.message}
              removeButtonAriaLabel="Remove Owner"
              onResolveSuggestions={onResolveSuggestions}
              onChange={(items: IServiceContact[]) =>
                onChange((items?.[0] as IServiceContact) ?? null)
              }
            />
          )}
        />
        <Controller
          control={control}
          name="status"
          defaultValue={selectedActionItem ? selectedActionItem.status : null}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field label="Status">
              <Textarea
                id="status"
                value={value}
                name={name}
                ref={ref}
                onBlur={onBlur}
                onChange={(ev, textareaData) => onChange(textareaData.value)}
              />
            </Field>
          )}
        />
        <Controller
          name="completed"
          control={control}
          defaultValue={!!selectedActionItem?.dateCompleted}
          rules={{
            validate: (value) => {
              if (!value) return true; // not completed => ok
              if (hasIncompleteTasks) {
                return 'All tasks must have a Completed Date before marking this action item as completed.';
              }
              return true;
            },
          }}
          render={({ onChange, value }) => (
            <Field label="Completed?" required validationMessage={errors?.completed?.message}>
              <RadioGroup
                id="completed"
                value={value ? 'true' : 'false'}
                onChange={(_, data) => {
                  // Clear dateCompleted if marking as not completed
                  const isCompleted = data.value === 'true';
                  onChange(isCompleted);

                  if (!isCompleted) {
                    setValue('dateCompleted', null);
                  }
                }}
              >
                <Radio value={yesOption.key} label={yesOption.text} />
                <Radio value={noOption.key} label={noOption.text} />
              </RadioGroup>
            </Field>
          )}
        />
        <Controller
          control={control}
          name="dateCompleted"
          defaultValue={selectedActionItem ? selectedActionItem.dateCompleted : null}
          rules={{
            validate: (value) =>
              validateDateCompleted(value, !!completed, latestTaskCompletedDate, 'an action item'),
          }}
          render={({ onChange, value }) => (
            <UtcDatePicker
              label="Date Completed"
              allowTextInput
              disableAutoFocus={false}
              disabled={!completed}
              isRequired={!!completed}
              value={value}
              textField={{ name: 'dateCompleted', errorMessage: errors?.dateCompleted?.message }}
              onSelectDate={(utcDateString: string) => onChange(utcDateString)}
              maxDate={today}
              minDate={minDateCompleted}
            />
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default EntityTerminationActionItemEditForm;
